import React, { useEffect, useRef, useState } from 'react';
import ServiceInquiryModal from './ServiceInquiryModal';
import './ServicesSection.css';
import { ServiceAPI } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import SkeletonCard from './SkeletonCard';

export default function ServicesSection({ variant }){
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [initialCount, setInitialCount] = useState(6);
  const [visibleCount, setVisibleCount] = useState(6);
  const gridRef = useRef(null);

  function openInquiry(service){
    setSelected(service);
    setOpen(true);
  }

  useEffect(()=>{
    const init = window.innerWidth <= 640 ? 3 : 6;
    setInitialCount(init);
    setVisibleCount(init);
  }, []);

  useEffect(()=>{
    let mounted = true;
    
    (async()=>{
      setLoading(true);
      setError('');
      try{
        console.log('🔍 Fetching services from API...');
        
        const fetchPromise = ServiceAPI.listPublic();
        const minTimePromise = new Promise(resolve => setTimeout(resolve, 500));
        
        const [response] = await Promise.all([
          fetchPromise,
          minTimePromise
        ]);

        console.log('✅ API Response received');
        
        let items = [];
        if (Array.isArray(response)) {
          items = response;
        } else if (response && typeof response === 'object') {
          items = response.items || response.data || response.services || [];
        }

        if(mounted && Array.isArray(items) && items.length > 0){
          const mapped = items
            .filter(s => {
              // Accept all services if published field doesn't exist
              if (s.published === undefined || s.published === null) return true;
              if (typeof s.published === 'boolean') return s.published === true;
              if (typeof s.published === 'string') return s.published === 'true' || s.published === '1';
              if (typeof s.published === 'number') return s.published === 1;
              return false;
            })
            .sort((a,b) => (a.order || 0) - (b.order || 0))
            .map(s => ({
              title: s.title || 'Untitled Service',
              desc: s.description || s.desc || '',
              slug: s.slug || '',
            }));
          
          console.log('✅ Final mapped services:', mapped.length, mapped);
          setServices(mapped);
        } else {
          console.warn('⚠️ No services found or empty array');
          setServices([]);
        }
      } catch(err) { 
        console.error('❌ Failed to load services:', err);
        if(mounted) {
          setError('Failed to load services. Please try again later.');
          setServices([]);
        }
      } finally { 
        if(mounted) setLoading(false); 
      }
    })();
    
    return ()=>{ mounted = false };
  }, []);

  // Note: We've removed IntersectionObserver in favor of Framer Motion variants
  // to perfectly sync the stagger entrance with AnimatePresence.
  useEffect(() => {
    // If there is any explicit DOM-based stuff needed, do it here. 
    // Otherwise, Framer Motion handles the reveal.
  }, [visibleCount, loading, services]);

  function loadMore(){
    const step = window.innerWidth <= 640 ? 3 : 6;
    setVisibleCount(c => {
      const next = Math.min(services.length, c + step);
      setTimeout(()=>{
        document.querySelector('.load-more-wrap')?.scrollIntoView({ 
          behavior:'smooth', 
          block:'center' 
        });
      }, 30);
      return next;
    });
  }
  
  function showLess(){
    setVisibleCount(initialCount);
  }

  const isHome = variant === 'home';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    },
    exit: { opacity: 0 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
  };

  return (
    <section id="services" className="services-section" aria-labelledby="services-title">
      <div className="container">
        <div className="services-head">
          <h2 id="services-title" className="services-title">Services</h2>
          <p className="services-sub">
            From initial design to ongoing management, our team delivers tailored 
            solutions that enhance user experience, boost conversions, and drive growth.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div 
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="services-grid"
            >
              {Array.from({ length: initialCount }).map((_, i) => (
                <SkeletonCard key={i} variant="services" />
              ))}
            </motion.div>
          ) : error ? (
            <motion.div 
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="text-center py-12"
            >
              <p className="text-red-500 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Retry
              </button>
            </motion.div>
          ) : services.length === 0 ? (
            <motion.div 
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="text-center py-12"
            >
              <p className="text-slate-600 mb-4">No services available yet.</p>
              <a 
                href="/admin/services" 
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors inline-block no-underline"
              >
                Add Services in Admin
              </a>
            </motion.div>
          ) : (
            <motion.div 
              key="content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="services-grid"
            >
              {(isHome ? services.slice(0,6) : services.slice(0, visibleCount)).map((s, i) => (
                <motion.article
                  variants={itemVariants}
                  className="service-card flex flex-col"
                  key={`${s.slug || s.title}-${i}`}
                  role="article"
                  tabIndex={0}
                >
                  <h3 className="service-title">{s.title}</h3>
                  <p className="service-desc mb-auto">{s.desc}</p>
                  
                  {/* HOME PAGE: Single button at bottom */}
                  {isHome && (
                    <a 
                      href="/services#services" 
                      className="service-link mt-auto"
                      aria-label={`View ${s.title} on Services page`}
                    >
                      <span className="icon" aria-hidden>
                        <svg viewBox="0 0 24 24">
                          <path 
                            d="M5 12h12M13 6l6 6-6 6" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      View on Services
                    </a>
                  )}

                  {/* SERVICES PAGE: Two centered buttons */}
                  {!isHome && (
                    <div className="flex gap-2 mt-auto pt-4 justify-center items-center">
                      {/* Learn More Button */}
                      {s.slug ? (
                        <a 
                          href={`/services/${s.slug}`}
                          className="service-link flex-1"
                          aria-label={`Learn more about ${s.title}`}
                        >
                          <span className="icon">
                            <svg viewBox="0 0 24 24" width="16" height="16">
                              <path 
                                d="M5 12h12M13 6l6 6-6 6" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          Learn More
                        </a>
                      ) : (
                        <button 
                          onClick={() => openInquiry(s)}
                          className="service-link flex-1"
                          aria-label={`Learn more about ${s.title}`}
                        >
                          <span className="icon">
                            <svg viewBox="0 0 24 24" width="16" height="16">
                              <path 
                                d="M5 12h12M13 6l6 6-6 6" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          Learn More
                        </button>
                      )}

                      {/* Get Quote Button - Primary */}
                      <button 
                        onClick={() => openInquiry(s)}
                        className="service-link service-link-primary flex-1"
                        aria-label={`Get quote for ${s.title}`}
                      >
                        <span className="icon">
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path 
                              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        Get Quote
                      </button>
                    </div>
                  )}
              </motion.article>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* View All Services - Home Page Only */}
        {!loading && !error && isHome && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '2.5rem auto 0 auto' }}>
            <a 
              href="/services#services" 
              className="btn cta-dark load-more-btn no-underline"
              style={{ margin: '0 auto' }}
            >
              <span className="inline-flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                  <path 
                    d="M5 12h14M12 5l7 7-7 7" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                View All Services
              </span>
            </a>
          </div>
        )}

        {/* Load More/Show Less - Services Page Only */}
        {!loading && !error && !isHome && services.length > visibleCount ? (
          <div className="load-more-wrap" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', marginTop: '2rem' }}>
            <button className="btn cta-dark load-more-btn" onClick={loadMore}>
              <span className="inline-flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                  <path 
                    d="M12 5v14M5 12h14" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                  />
                </svg>
                Load more…
              </span>
            </button>
            <div className="progress-hint">
              {`${visibleCount} of ${services.length} shown`}
            </div>
          </div>
        ) : !loading && !error && !isHome && services.length > initialCount ? (
          <div className="load-more-wrap" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', marginTop: '2rem' }}>
            <button className="btn cta-dark load-more-btn" onClick={showLess}>
              <span className="inline-flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                  <path 
                    d="M5 12h14" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                  />
                </svg>
                Show less
              </span>
            </button>
            <div className="progress-hint">
              {`${visibleCount} of ${services.length} shown`}
            </div>
          </div>
        ) : null}
      </div>
      {!isHome && (
        <ServiceInquiryModal 
          open={open} 
          onClose={()=>setOpen(false)} 
          service={selected} 
        />
      )}
    </section>
  );
}         